import { useState, useEffect, useRef } from 'react';
import {
	ActionBarWrapper,
	ActionBarAnimationWrapper,
	ActionBarContent,
	ContentWrap,
	Text,
	CloseButton,
	Divider,
	StyledButton
} from './ActionBar.styled';
import { ButtonVariant } from '../button';
import { Icon } from '../icon/Icon';
import type { IconName } from '../icon';

interface Action {
    text: string;
    type?: ButtonVariant;
    icon: IconName;
    onClick: () => void;
}

interface Props {
    onClose?: () => void;
    itemsCount?: number;
    actions?: Action[];
    visible?: boolean;
}

const ANIMATION_DURATION_MS = 300;

export const ActionBar = ({ 
	itemsCount,
	onClose,
	actions = [],
	visible = true,
}: Props) => {
	const [isExiting, setIsExiting] = useState(false);
	const wasVisible = useRef(visible);
	const wasItemsCount = useRef(itemsCount);

	useEffect(() => {
		if (wasVisible.current && !visible) {
			setIsExiting(true);
			setTimeout(() => {
				onClose?.();
			}, ANIMATION_DURATION_MS);
		}
		wasVisible.current = visible;
	}, [visible, onClose]);

	useEffect(() => {
		// Handle hiding when itemsCount becomes 0
		if (wasItemsCount.current && wasItemsCount.current > 0 && (!itemsCount || itemsCount === 0)) {
			setIsExiting(true);
			setTimeout(() => {
				onClose?.();
			}, ANIMATION_DURATION_MS);
		}
		// Handle showing when itemsCount becomes greater than 0
		else if ((!wasItemsCount.current || wasItemsCount.current === 0) && itemsCount && itemsCount > 0) {
			setIsExiting(false);
		}
		wasItemsCount.current = itemsCount;
	}, [itemsCount, onClose]);

	useEffect(() => {
		if (visible && itemsCount && itemsCount > 0) {
			setIsExiting(false);
		}
	}, [visible, itemsCount]);

	const handleClose = () => {
		setIsExiting(true);
		setTimeout(() => {
			onClose?.();
		}, ANIMATION_DURATION_MS);
	};

	// Don't render if not visible and not in exit animation
	if (!visible && !isExiting) return null;

	// Don't render if no items and not in exit animation
	if ((!itemsCount || itemsCount === 0) && !isExiting) return null;

	return (
		<ActionBarWrapper>
			<ActionBarAnimationWrapper $isExiting={isExiting}>
				<ActionBarContent>
					<ContentWrap>
						<Text>
							{itemsCount} selected
						</Text>
						<Divider />
						{actions.map((action, idx) => (
							<StyledButton
								key={action.text + idx}
								variant={action.type || 'primary'}
								icon={action.icon}
								label={action.text}
								onClick={action.onClick}
								size="small"
								$variant={action.type || 'primary'}
							/>
						))}
					</ContentWrap>
				</ActionBarContent>
				<CloseButton onClick={handleClose}>
					<Icon icon="x-slim" />
				</CloseButton>
			</ActionBarAnimationWrapper>
		</ActionBarWrapper>
	);
};
